/**
 * Join Room Page
 *
 * Enter room link/code or scan QR to join.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom'
import { parseRoomLink, parseSecureShareQR } from '../lib/crypto'
import { QRScanner } from '../components/common'
import { generateAnonName } from '../lib/names'

/**
 * Decode URL-safe base64
 */
function decodeUrlSafeBase64(str: string): string {
  let padded = str.replace(/-/g, '+').replace(/_/g, '/')
  while (padded.length % 4) {
    padded += '='
  }
  return atob(padded)
}

/**
 * Valid hierarchy types for rooms
 */
const VALID_HIERARCHY_TYPES = ['standalone', 'command', 'sector'] as const
type HierarchyType = typeof VALID_HIERARCHY_TYPES[number]

/**
 * Validate and return a hierarchy type, or undefined if invalid
 */
function validateHierarchyType(value: string | null | undefined): HierarchyType | undefined {
  if (value && VALID_HIERARCHY_TYPES.includes(value as HierarchyType)) {
    return value as HierarchyType
  }
  return undefined
}

interface QRData {
  roomId: string
  roomSecret: string
  roomName: string
  pin?: string
  commandRoomId?: string
  hierarchyType?: HierarchyType
}

export function JoinPage() {
  const navigate = useNavigate()
  const { roomId: paramRoomId } = useParams()
  const [searchParams] = useSearchParams()

  // Form state
  const [roomLink, setRoomLink] = useState('')
  const [roomSecret, setRoomSecret] = useState('')
  const [roomPin, setRoomPin] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showScanner, setShowScanner] = useState(false)

  // QR data state - when we have data from QR code
  const [qrData, setQrData] = useState<QRData | null>(null)

  // For manual entry flow
  const [manualRoomId, setManualRoomId] = useState<string | null>(null)

  // Parse QR data from URL on mount
  useEffect(() => {
    // Restore display name from session or generate anonymous name
    const savedName = sessionStorage.getItem('displayName')
    if (savedName) {
      setDisplayName(savedName)
    } else {
      setDisplayName(generateAnonName())
    }

    // Get hierarchy params from URL (for sector joins)
    const cmdParam = searchParams.get('cmd')
    const hierarchyTypeParam = validateHierarchyType(searchParams.get('hierarchyType'))

    // Check for encoded data in 'd' parameter (from QR code scanned with phone camera)
    const encodedData = searchParams.get('d')
    if (encodedData) {
      try {
        const jsonStr = decodeUrlSafeBase64(encodedData)
        const data = JSON.parse(jsonStr)
        console.log('[Join] Parsed QR data from URL:', {
          roomId: data.r,
          roomName: data.n,
          hasSecret: !!data.s,
          hasPin: !!data.p,
          commandRoomId: cmdParam || data.commandRoomId,
          hierarchyType: hierarchyTypeParam || validateHierarchyType(data.hierarchyType),
        })

        if (data.v === 1 && data.r && data.s && data.n) {
          // Store QR data for form pre-fill
          // Hierarchy params can come from URL query params OR from the encoded data
          setQrData({
            roomId: data.r,
            roomSecret: data.s,
            roomName: data.n,
            pin: data.p,
            commandRoomId: cmdParam || data.commandRoomId,
            hierarchyType: hierarchyTypeParam || data.hierarchyType,
          })
          // Pre-fill PIN if provided
          if (data.p) {
            setRoomPin(data.p)
          }
          return
        }
      } catch (e) {
        console.error('[Join] Failed to parse QR data from URL:', e)
        setError('Invalid QR code data in URL')
      }
    }

    // Check for room link in URL query param
    const linkParam = searchParams.get('link')
    if (linkParam) {
      setRoomLink(decodeURIComponent(linkParam))
    }

    // If we have a roomId in URL path (legacy format), check for hash with secret
    if (paramRoomId && window.location.hash) {
      const secret = window.location.hash.slice(1)
      if (secret) {
        setManualRoomId(paramRoomId)
        setRoomSecret(secret)
      }
    } else if (paramRoomId) {
      // Just roomId in URL, need secret manually
      setManualRoomId(paramRoomId)
    }
  }, [paramRoomId, searchParams])

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!displayName.trim()) {
      setError('Display name is required')
      return
    }

    // Store display name
    sessionStorage.setItem('displayName', displayName.trim())

    // Case 1: We have QR data (from URL or scanner)
    if (qrData) {
      const { roomId, roomSecret: qrSecret, pin: qrPin, commandRoomId, hierarchyType } = qrData
      const effectivePin = roomPin.trim() || qrPin // Allow user to override/enter PIN

      console.log('[Join] Joining room from QR data:', {
        roomId,
        hasSecret: !!qrSecret,
        hasPin: !!effectivePin,
        commandRoomId,
        hierarchyType,
      })

      // Store credentials
      sessionStorage.setItem(`room_${roomId}_secret`, qrSecret)
      if (effectivePin) {
        sessionStorage.setItem(`room_${roomId}_pin`, effectivePin)
      }
      if (!sessionStorage.getItem(`room_${roomId}_role`)) {
        sessionStorage.setItem(`room_${roomId}_role`, 'participant')
      }

      // Store hierarchy data for sector rooms
      // Clear stale data if not present in current join to avoid using outdated values
      if (hierarchyType) {
        sessionStorage.setItem(`room_${roomId}_hierarchyType`, hierarchyType)
        console.log('[Join] Stored hierarchyType:', hierarchyType)
      } else {
        sessionStorage.removeItem(`room_${roomId}_hierarchyType`)
      }
      if (commandRoomId) {
        sessionStorage.setItem(`room_${roomId}_commandRoomId`, commandRoomId)
        console.log('[Join] Stored commandRoomId:', commandRoomId)
      } else {
        sessionStorage.removeItem(`room_${roomId}_commandRoomId`)
      }

      navigate(`/room/${roomId}`)
      return
    }

    // Case 2: We have a manual roomId (from URL path)
    if (manualRoomId) {
      if (!roomSecret.trim()) {
        setError('Room secret is required. Get it from the room organizer.')
        return
      }

      console.log('[Join] Joining room from manual entry:', { roomId: manualRoomId })

      sessionStorage.setItem(`room_${manualRoomId}_secret`, roomSecret.trim())
      if (roomPin.trim()) {
        sessionStorage.setItem(`room_${manualRoomId}_pin`, roomPin.trim())
      }
      if (!sessionStorage.getItem(`room_${manualRoomId}_role`)) {
        sessionStorage.setItem(`room_${manualRoomId}_role`, 'participant')
      }

      navigate(`/room/${manualRoomId}`)
      return
    }

    // Case 3: Parse room link manually entered
    if (!roomLink.trim()) {
      setError('Room link is required')
      return
    }

    // Try parsing as secure QR format first (in case user pasted QR URL)
    const secureData = parseSecureShareQR(roomLink.trim())
    if (secureData) {
      setQrData(secureData)
      if (secureData.pin) {
        setRoomPin(secureData.pin)
      }
      // Re-submit with QR data
      return
    }

    // Try parsing as room link
    const parsed = parseRoomLink(roomLink.trim())
    if (!parsed) {
      setError('Invalid room link format')
      return
    }

    // Check if secret is in URL (legacy format with hash)
    if (parsed.roomSecret) {
      console.log('[Join] Joining room from legacy link:', { roomId: parsed.roomId })

      sessionStorage.setItem(`room_${parsed.roomId}_secret`, parsed.roomSecret)
      if (roomPin.trim()) {
        sessionStorage.setItem(`room_${parsed.roomId}_pin`, roomPin.trim())
      }
      if (!sessionStorage.getItem(`room_${parsed.roomId}_role`)) {
        sessionStorage.setItem(`room_${parsed.roomId}_role`, 'participant')
      }
      navigate(`/room/${parsed.roomId}`)
    } else {
      // Secure format - need to enter secret manually
      setManualRoomId(parsed.roomId)
      setError('This link requires a room secret. Enter it below.')
    }
  }

  const handleQRScan = useCallback(
    (data: string) => {
      setShowScanner(false)
      setError(null)

      console.log('[Join] QR scanned:', data.substring(0, 50) + '...')

      // Try to parse as secure QR format (URL with encoded data or raw JSON)
      const secureData = parseSecureShareQR(data)
      if (secureData) {
        console.log('[Join] Parsed secure QR:', {
          roomId: secureData.roomId,
          roomName: secureData.roomName,
          commandRoomId: secureData.commandRoomId,
          hierarchyType: secureData.hierarchyType,
        })
        setQrData(secureData)
        if (secureData.pin) {
          setRoomPin(secureData.pin)
        }
        return
      }

      // Fall back to legacy URL format
      const parsed = parseRoomLink(data)
      if (!parsed) {
        setError('Invalid QR code. Please scan a valid CrowdTalkie room QR code.')
        return
      }

      // Set the room link so user can see what was scanned
      setRoomLink(data)

      if (parsed.roomSecret) {
        // Legacy format with secret in hash
        setQrData({
          roomId: parsed.roomId,
          roomSecret: parsed.roomSecret,
          roomName: `Room ${parsed.roomId.substring(0, 8)}...`,
        })
      } else {
        // No secret - need manual entry
        setManualRoomId(parsed.roomId)
        setError('QR code does not contain room secret. Enter it below.')
      }
    },
    []
  )

  // Determine what to show
  const showQrInfo = !!qrData
  const needsSecretEntry = !qrData && !!manualRoomId

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative">
      {/* Header */}
      <header className="flex-shrink-0 flex items-center gap-4 p-4 border-b border-white/10 glass-panel z-10">
        <Link
          to="/"
          className="text-white/60 hover:text-primary transition-colors text-sm font-mono tracking-wider"
        >
          [ BACK ]
        </Link>
        <h1 className="text-lg font-display font-bold uppercase tracking-widest text-primary neon-glow">Join Room</h1>
      </header>

      {/* Form */}
      <main className="flex-1 min-h-0 p-4 overflow-y-auto scrollbar-hide space-y-4">
        <form onSubmit={handleJoin} className="space-y-6">

          {/* QR Data Info - when we have data from QR code */}
          {showQrInfo && qrData && (
            <div className="p-4 rounded-xl bg-primary/10 border border-primary/40 neon-border">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-primary text-xl">✓</span>
                <span className="text-sm text-primary font-bold font-mono uppercase">
                  {qrData.hierarchyType === 'sector' ? 'Sector Found' : 'Room Found'}
                </span>
              </div>
              <div className="text-2xl font-display font-bold text-white tracking-wide">{qrData.roomName}</div>
              <div className="text-[10px] text-white/40 mt-1 font-mono truncate border-l-2 border-primary/30 pl-2">
                {qrData.hierarchyType === 'sector' ? 'Sector' : 'Room'} ID: {qrData.roomId}
              </div>
              {qrData.hierarchyType === 'sector' && qrData.commandRoomId && (
                <div className="text-xs text-teal mt-2 font-mono flex items-center gap-1">
                  <span>📡</span> Linked to Command Room
                </div>
              )}
              {qrData.pin && (
                <div className="text-xs text-primary/80 mt-2 font-mono">
                  PIN required
                </div>
              )}
            </div>
          )}

          {/* Room Link Input - only when no QR data and no roomId from URL */}
          {!showQrInfo && !manualRoomId && (
            <div className="glass-panel p-4 rounded-xl border border-white/5">
              <label className="block text-xs font-bold text-primary mb-2 uppercase tracking-wider">
                Room Link
              </label>
              <input
                type="text"
                value={roomLink}
                onChange={(e) => setRoomLink(e.target.value)}
                placeholder="Paste link here..."
                className="input bg-black/20 border-white/10 focus:border-primary/50 focus:neon-border font-mono text-sm py-3"
              />
              <p className="text-[10px] text-white/30 mt-1 font-mono">
                Get this from the room organizer
              </p>
            </div>
          )}

          {/* Manual Room ID Info */}
          {!showQrInfo && manualRoomId && (
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="text-xs text-white/60 font-mono uppercase tracking-wider mb-1">Room</div>
              <div className="text-lg font-bold font-mono truncate text-primary/80">{manualRoomId}</div>
            </div>
          )}

          {/* Display Name */}
          <div className="glass-panel p-4 rounded-xl border border-white/5">
            <label className="block text-xs font-bold text-primary mb-2 uppercase tracking-wider">
              Your Name
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., Sarah"
                className="input flex-1 bg-black/20 border-white/10 focus:border-primary/50 focus:neon-border font-mono text-sm py-3"
                maxLength={30}
              />
              <button
                type="button"
                onClick={() => setDisplayName(generateAnonName())}
                className="px-3 py-2 rounded-lg bg-white/5 text-primary hover:bg-primary/20 transition-colors border border-primary/30"
                title="Generate random name"
              >
                🎲
              </button>
            </div>
            <p className="text-[10px] text-white/30 mt-1 font-mono">
              Visible to others in the room
            </p>
          </div>

          {/* Room Secret & PIN - when needed for manual entry */}
          {needsSecretEntry && (
            <div className="glass-panel p-4 rounded-xl border border-yellow-500/30 bg-yellow-500/5">
              <div className="mb-4">
                <label className="block text-xs font-bold text-yellow-500 mb-2 uppercase tracking-wider">
                  Room Secret
                </label>
                <input
                  type="text"
                  value={roomSecret}
                  onChange={(e) => setRoomSecret(e.target.value)}
                  placeholder="Enter secret..."
                  className="input font-mono bg-black/20 border-yellow-500/20 focus:border-yellow-500/50 py-3 text-sm"
                />
                <p className="text-[10px] text-yellow-500/70 mt-1 font-mono flex items-center gap-1">
                  <span>⚠</span> Get this from the room organizer
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-yellow-500 mb-2 uppercase tracking-wider">
                  PIN <span className="text-yellow-500/50 normal-case">(if required)</span>
                </label>
                <input
                  type="text"
                  value={roomPin}
                  onChange={(e) => setRoomPin(e.target.value)}
                  placeholder="Enter PIN..."
                  className="input font-mono bg-black/20 border-yellow-500/20 focus:border-yellow-500/50 py-3 text-sm tracking-widest"
                  maxLength={10}
                />
              </div>
            </div>
          )}

          {/* PIN Override */}
          {showQrInfo && !qrData?.pin && (
            <div className="glass-panel p-4 rounded-xl border border-white/5">
              <label className="block text-xs font-bold text-primary mb-2 uppercase tracking-wider">
                PIN <span className="text-white/40 normal-case">(if required)</span>
              </label>
              <input
                type="text"
                value={roomPin}
                onChange={(e) => setRoomPin(e.target.value)}
                placeholder="Enter PIN..."
                className="input font-mono bg-black/20 border-white/10 focus:border-primary/50 focus:neon-border py-3 text-sm tracking-widest"
                maxLength={10}
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/50 text-red-500 text-xs font-mono flex items-center gap-2">
              <span className="text-lg">⚠</span> {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            className="w-full py-4 rounded-xl bg-primary text-black font-bold text-lg shadow-[0_0_15px_theme('colors.primary')] hover:shadow-[0_0_25px_theme('colors.primary')] hover:scale-[1.01] active:scale-[0.99] transition-all uppercase tracking-widest neon-border"
            style={{ marginBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          >
            Join Room
          </button>
        </form>

        {/* QR Scanner Button */}
        {!showQrInfo && !manualRoomId && (
          <div className="mt-8 relative">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-white/5"></div>
            <div className="relative text-center">
              <span className="px-2 bg-tactical-dark text-white/30 text-[10px] uppercase font-mono tracking-widest">Or</span>
            </div>

            <button
              type="button"
              className="w-full mt-4 py-4 rounded-xl bg-white/5 text-white font-bold text-sm border border-white/10 hover:bg-white/10 transition-colors uppercase tracking-widest flex items-center justify-center gap-2"
              onClick={() => setShowScanner(true)}
            >
              <span className="text-xl">📷</span> Scan QR Code
            </button>
          </div>
        )}

        {/* QR Scanner Modal */}
        {showScanner && (
          <QRScanner
            onScan={handleQRScan}
            onClose={() => setShowScanner(false)}
            onError={(err) => console.error('QR scan error:', err)}
          />
        )}
      </main>
    </div>
  )
}
